import React from 'react'
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Image,
} from '@react-pdf/renderer'
import logoPng from './logo-horizontal.png'

const teal = '#2a9d8f'
const styles = StyleSheet.create({
  page: {
    fontFamily: 'Helvetica',
    fontSize: 10,
    color: '#1a1a1a',
    paddingHorizontal: 40,
    paddingTop: 32,
    paddingBottom: 72,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
    paddingBottom: 12,
    borderBottomWidth: 2,
    borderBottomColor: teal,
  },
  logo: { width: 160, height: 40 },
  address: { width: 290, textAlign: 'right', fontSize: 9, color: '#555' },
  title: {
    fontSize: 14,
    fontFamily: 'Helvetica-Bold',
    textAlign: 'center',
    marginBottom: 16,
  },
  infoCard: {
    marginBottom: 16,
    padding: 14,
    borderWidth: 1,
    borderColor: '#d6e8e5',
    borderRadius: 6,
    backgroundColor: '#f7faf9',
  },
  infoLine: { flexDirection: 'row', alignItems: 'flex-start' },
  infoCell: { flex: 1, paddingRight: 12 },
  infoLabel: { fontSize: 8, color: '#666', marginBottom: 5 },
  infoValue: { fontSize: 11, fontFamily: 'Helvetica-Bold' },
  infoDivider: {
    marginVertical: 12,
    borderTopWidth: 1,
    borderTopColor: '#d6e8e5',
  },
  statusBadge: {
    alignSelf: 'flex-start',
    paddingHorizontal: 9,
    paddingVertical: 5,
    borderRadius: 4,
    fontSize: 9,
    fontFamily: 'Helvetica-Bold',
  },
  row: {
    flexDirection: 'row',
    paddingVertical: 6,
    borderBottomWidth: 0.5,
    borderBottomColor: '#e0e0e0',
  },
  tableHeader: { fontSize: 8, color: '#555', fontFamily: 'Helvetica-Bold' },
  product: { flex: 1, paddingRight: 8 },
  quantity: { width: 45, textAlign: 'center' },
  money: { width: 95, textAlign: 'right' },
  totals: {
    marginTop: 20,
    padding: 14,
    backgroundColor: '#f7faf9',
    borderWidth: 1,
    borderColor: '#d6e8e5',
    borderRadius: 6,
  },
  totalRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: 5,
  },
  final: { color: teal, fontFamily: 'Helvetica-Bold', fontSize: 13 },
  footer: {
    position: 'absolute',
    flexDirection: 'row',
    justifyContent: 'space-between',
    bottom: 32,
    left: 40,
    right: 40,
    paddingTop: 8,
    borderTopWidth: 1,
    borderTopColor: '#e0e0e0',
    fontSize: 8,
    color: '#555',
  },
})

const currency = (value) =>
  Number(value || 0).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  })

export default function PdfPontoDeVenda({
  idPedidoVenda,
  nomePaciente = '',
  idAtendimento,
  statusPedido = '0',
  snCobraCheckout = 'N',
  itens = [],
  desconto = 0,
  emissaoData = '',
  formasPagamento = [],
  dsObservacoes = '',
}) {
  const subtotal = itens.reduce(
    (total, item) => total + Number(item.vlTotal || 0),
    0,
  )
  const total = Math.max(0, subtotal - Number(desconto || 0))

  return (
    <Document
      title={`Ponto de venda${idPedidoVenda ? ` - ${idPedidoVenda}` : ''}`}
    >
      <Page size="A4" style={styles.page}>
        <View style={styles.header}>
          <Image alt="Spa Tour Life" src={logoPng} style={styles.logo} />
          <View style={styles.address}>
            <Text>Dr. Édio Schaurich - CRM/RS 19557</Text>
            <Text>
              Rua Buarque de Macedo, 1030/1000 - Progresso, Montenegro - RS
            </Text>
          </View>
        </View>
        <Text style={styles.title}>PONTO DE VENDA</Text>
        <View style={styles.infoCard} wrap={false}>
          <View style={styles.infoLine}>
            <View style={styles.infoCell}>
              <Text style={styles.infoLabel}>VENDA</Text>
              <Text style={[styles.infoValue, { color: teal }]}>
                {idPedidoVenda ? `#${idPedidoVenda}` : 'Em preparação'}
              </Text>
            </View>
            {nomePaciente && (
              <View style={[styles.infoCell, { flex: 2 }]}>
                <Text style={styles.infoLabel}>PACIENTE</Text>
                <Text style={styles.infoValue}>{nomePaciente}</Text>
              </View>
            )}
          </View>
          <View style={styles.infoDivider} />
          <View style={styles.infoLine}>
            {idAtendimento != null && (
              <View style={styles.infoCell}>
                <Text style={styles.infoLabel}>PROPOSTA</Text>
                <Text style={styles.infoValue}>#{idAtendimento}</Text>
              </View>
            )}
            <View style={[styles.infoCell, { flex: 2 }]}>
              <Text style={styles.infoLabel}>STATUS DO PEDIDO</Text>
              <Text
                style={[
                  styles.statusBadge,
                  {
                    color: statusPedido === '9' ? '#666' : teal,
                    backgroundColor:
                      statusPedido === '9' ? '#e9e9e9' : '#e5f4f1',
                  },
                ]}
              >
                {{
                  0: 'Em preparação',
                  1: 'Pronto para cobrar',
                  2: 'Cobrado',
                  9: 'Cancelado',
                }[statusPedido] || 'Não informado'}
              </Text>
            </View>
          </View>
        </View>
        {(dsObservacoes || '').trim() && (
          <View style={{ marginBottom: 12 }}>
            <Text
              style={[styles.tableHeader, { marginBottom: 6 }]}
              minPresenceAhead={20}
            >
              OBSERVAÇÕES
            </Text>
            <Text style={{ lineHeight: 1.4 }}>{dsObservacoes}</Text>
          </View>
        )}
        <View style={[styles.row, styles.tableHeader]}>
          <Text style={styles.product}>Produto</Text>
          <Text style={styles.quantity}>Qtd</Text>
          <Text style={styles.money}>Valor unitário</Text>
          <Text style={styles.money}>Valor total</Text>
        </View>
        {itens.map((item, index) => (
          <View key={index} style={styles.row} wrap={false}>
            <Text style={styles.product}>{item.dsProduto}</Text>
            <Text style={styles.quantity}>{item.qtProduto}</Text>
            <Text style={styles.money}>{currency(item.vlUnitario)}</Text>
            <Text style={styles.money}>{currency(item.vlTotal)}</Text>
          </View>
        ))}
        <View style={styles.totals} wrap={false}>
          {snCobraCheckout === 'S' && (
            <Text style={[styles.final, { marginBottom: 8 }]}>
              Cobrar no checkout
            </Text>
          )}
          <View style={styles.totalRow}>
            <Text>Subtotal</Text>
            <Text>{currency(subtotal)}</Text>
          </View>
          <View style={styles.totalRow}>
            <Text>Desconto (-)</Text>
            <Text>{currency(desconto)}</Text>
          </View>
          <View style={[styles.totalRow, styles.final]}>
            <Text>Total da venda</Text>
            <Text>{currency(total)}</Text>
          </View>
          {formasPagamento.length > 0 && (
            <View style={{ marginTop: 12 }}>
              <Text style={styles.tableHeader}>FORMAS DE PAGAMENTO</Text>
              {formasPagamento.map((pagamento, index) => (
                <View key={index} style={styles.totalRow} wrap={false}>
                  <Text style={styles.product}>
                    {pagamento.nome}
                    {Number(pagamento.qtParcelas) > 0
                      ? ` (${pagamento.qtParcelas}x)`
                      : ''}
                  </Text>
                  <Text style={styles.money}>{currency(pagamento.vlPago)}</Text>
                </View>
              ))}
            </View>
          )}
        </View>
        <View style={styles.footer} fixed>
          <Text>SPA TOUR LIFE - APART HOTEL LTDA | 3Sync Sistemas</Text>
          <Text>
            {emissaoData ? `Emitido em Montenegro ${emissaoData}` : ''}
          </Text>
        </View>
      </Page>
    </Document>
  )
}
